import jStat from 'jstat';

export type QuartileMethod = 'exclusive' | 'inclusive' | 'interpolation' | 'closest';

export interface QuartileOptions {
  /**
   * The method to use for quartile calculation
   * - 'exclusive': Exclusive method (Excel) - excludes median from quartile calculation
   * - 'inclusive': Inclusive method - includes median in quartile calculation
   * - 'interpolation': Linear interpolation method (R type 7)
   * - 'closest': Closest rank method
   */
  method?: QuartileMethod;
}

export interface IQRResult {
  q1: number;
  q3: number;
  iqr: number;
  lowerFence: number;
  upperFence: number;
  outliers: number[];
  nonOutliers: number[];
}

/**
 * Calculate quartiles using various methods
 * @param data Array of numbers to calculate quartiles from
 * @param options Configuration options for quartile calculation
 * @returns Object containing Q1 and Q3 values
 */
function calculateQuartiles(data: readonly number[], options: QuartileOptions = {}): { q1: number; q3: number } {
  const { method = 'exclusive' } = options;
  const n = data.length;
  const sortedData = [...data].sort((a, b) => a - b);

  // For arrays of length 1, both quartiles are the same value
  if (n === 1) {
    return { q1: sortedData[0]!, q3: sortedData[0]! };
  }

  switch (method) {
    case 'exclusive': {
      // Exclusive method (Excel): Split into halves excluding median
      const midIndex = Math.floor(n / 2);
      const lowerHalf = sortedData.slice(0, midIndex);
      const upperHalf = sortedData.slice(n % 2 === 0 ? midIndex : midIndex + 1);
      return {
        q1: jStat.median(lowerHalf),
        q3: jStat.median(upperHalf)
      };
    }
    case 'inclusive': {
      // Inclusive method: Use quartiles function that includes median
      const quartiles = jStat.quartiles(sortedData);
      return {
        q1: quartiles[0],
        q3: quartiles[2]
      };
    }
    case 'interpolation': {
      // Linear interpolation method (R type 7)
      const q1Position = (n - 1) * 0.25;
      const q3Position = (n - 1) * 0.75;
      const q1Floor = Math.floor(q1Position);
      const q3Floor = Math.floor(q3Position);
      const q1Fraction = q1Position - q1Floor;
      const q3Fraction = q3Position - q3Floor;
      
      return {
        q1: sortedData[q1Floor]! + q1Fraction * (sortedData[q1Floor + 1]! - sortedData[q1Floor]!),
        q3: sortedData[q3Floor]! + q3Fraction * (sortedData[q3Floor + 1]! - sortedData[q3Floor]!)
      };
    }
    case 'closest': {
      // Closest rank method: Round to nearest rank
      const q1Index = Math.round(n * 0.25) - 1;
      const q3Index = Math.round(n * 0.75) - 1;
      return {
        q1: sortedData[q1Index]!,
        q3: sortedData[q3Index]!
      };
    }
    default: {
      // Default to exclusive method if unknown method specified
      const midIndex = Math.floor(n / 2);
      const lowerHalf = sortedData.slice(0, midIndex);
      const upperHalf = sortedData.slice(n % 2 === 0 ? midIndex : midIndex + 1);
      return {
        q1: jStat.median(lowerHalf),
        q3: jStat.median(upperHalf)
      };
    }
  }
}

/**
 * Calculate IQR and identify outliers for a dataset
 * @param data Array of numbers to analyze
 * @param options Configuration options for quartile calculation
 * @returns Object containing quartile statistics and outlier information
 */
export function calculateIQR(data: readonly number[], options: QuartileOptions = {}): IQRResult {
  if (data.length <= 1) {
    const value = data[0] ?? 0;
    return {
      q1: value,
      q3: value,
      iqr: 0,
      lowerFence: value,
      upperFence: value,
      outliers: [],
      nonOutliers: data.length === 1 ? [value] : []
    };
  }

  // Sort data for outlier detection
  const sortedData = [...data].sort((a, b) => a - b);
  
  // Calculate quartiles using specified method
  const { q1, q3 } = calculateQuartiles(sortedData, options);
  const iqr = q3 - q1;
  
  // Calculate fences for outlier detection
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;
  
  // Identify outliers and non-outliers
  const outliers = sortedData.filter(x => x < lowerFence || x > upperFence);
  const nonOutliers = sortedData.filter(x => x >= lowerFence && x <= upperFence);
  
  return {
    q1,
    q3,
    iqr,
    lowerFence,
    upperFence,
    outliers,
    nonOutliers
  };
} 